import React, { useState, useCallback } from "react";
import { Container, Card, CardHeader, CardBody } from "reactstrap";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { PdfPreviewContainer } from "./PdfPreview";
import { TratoDocumentPreview } from "./TratoDocumentPreview";
import Portal from "../Portal";

import {
  useEditor,
  useEditorState,
  useEditorActions,
  UPDATE_SIGNATURE,
  REMOVE_SIGNATURE,
} from "../../context/editor";
import { useModals, CLOSE_PREVIEW } from "../../context/modals";

export default function DocumentPreview({
  className,
  document: doc,
  isPdf,
  isEditable,
  currentPage,
  participants,
  signatures,
  BaseElementComponent,
  card,
  isPremium,
  width,
  isPreview,
  header = true,
  onUpdateSignature,
  onRemoveSignature,
  max
}) {
  const { t } = useTranslation("common");

  const renderContent = () => {
    return isPdf ? (
      <PdfPreviewContainer
        document={doc}
        url={doc?.importedPdf}
        isEditable={isEditable}
        signatures={signatures}
        width={width}
        participants={participants}
        currentPage={currentPage}
        onUpdateSignature={onUpdateSignature}
        onRemoveSignature={onRemoveSignature}
      />
    ) : (
      <TratoDocumentPreview
        document={doc}
        width={width}
        isPreview={isPreview}
        isPremium={isPremium}
        participants={participants}
        BaseElementComponent={BaseElementComponent}
      />
    );
  };

  if (card) {
    return (
      <Container
        fluid
        className={clsx("d-flex justify-content-center overflow-hidden", className)}
        style={{ width: "100%" /*maxHeight: 900*/ }}
      >
        <Card
          className={clsx(
            "my-3 shadow-sm editorPreviewContainer",
            isPdf && "mw-700"
          )}
        >
          {header && (
            <CardHeader>
              <h4 className="font-weight-bold mb-0">{doc.name}</h4>
              <p className="text-muted mb-0">{t("preview")}</p>
            </CardHeader>
          )}
          <CardBody
            className={clsx(
              "contract-preview",
              isPdf && "p-0",
              doc.isMigrated && "p-0",
              !doc.isMigrated && "overflow-auto"
            )}
            style={{ minHeight: 500 }}
          >
            {renderContent()}
          </CardBody>
        </Card>
      </Container>
    );
  }

  return <>{renderContent()}</>;
}

export function EditorDocumentPreview(props) {
  const [
    { document: doc, isPdf, isEditable, currentPage, participants, signatures },
    dispatch,
  ] = useEditor();

  const handleUpdateSignature = (participantId, signature) => {
    dispatch({ type: UPDATE_SIGNATURE, participantId, signature });
  };

  const handleRemoveSignature = (participantId, signatureId) => {
    dispatch({ type: REMOVE_SIGNATURE, participantId, signatureId });
  };

  return (
    <DocumentPreview
      {...props}
      document={doc}
      isPdf={isPdf}
      isEditable={isEditable}
      currentPage={currentPage}
      participants={participants}
      signatures={signatures}
      isPreview={false}
      BaseElementComponent={EditorBaseElement}
      onUpdateSignature={handleUpdateSignature}
      onRemoveSignature={handleRemoveSignature}
    />
  );
}

export const EditorBaseElement = ({ element, children }) => {
  const { document: contract, currentElement } = useEditorState();
  const { setCurrentElement } = useEditorActions();

  const currentElementId = currentElement._id;

  const handleClick = () => {
    setCurrentElement(element);
  };

  return (
    <div
      className={clsx(
        "p-2 ",
        currentElementId === element._id && "border border-accent"
      )}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};
export function DocumentPreviewModal({ editable }) {
  const [modalsState, dispatch] = useModals();
  const { t } = useTranslation("common");
  const [numPages, setNumPages] = useState(1);

  const handleClose = useCallback(() => {
    dispatch({ type: CLOSE_PREVIEW });
  }, [dispatch]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  if (!modalsState.preview) {
    return null;
  }

  console.log({ modalsState });

  return (
    <Portal selectorId="#modals">
      <div
        id="contract-preview"
        className={clsx(
          "position-fixed bg-light border rounded",
          modalsState.center && "center",
          modalsState.template?.pluginType === "pdf" && "overflow-auto"
        )}
        style={{ userSelect: "none" }}
      >
        <div className="bg-grey p-4 border-bottom rounded-top">
          <div className="row">
            <div className="col-md-8 col-8">
              <h5 className="font-weight-bold mb-0">
                {modalsState.template.name}
              </h5>
              <p className="mb-0 text-muted">{t("preview")}</p>
            </div>
            <div className="col-md-4 col-4">
              <div
                className="btn btn-primary btn-sm pointer float-right"
                onClick={handleClose}
              >
                {t("actions.close")}
                <FontAwesomeIcon
                  icon={"times-circle"}
                  className="ml-2"
                  inverse
                />
              </div>
            </div>
          </div>
        </div>
        {modalsState.template?.pluginType !== "pdf" && (
          <DocumentPreview document={modalsState.template} width={700} isPremium isPreview />
        )}
        {modalsState.template?.pluginType === "pdf" && (
          <Document
            file={modalsState.template.importedPdf}
            options={{
    cMapUrl: `cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: 'standard_fonts/',
}}
            onLoadSuccess={onDocumentLoadSuccess}
            style={
              {
                // width: '200px',
              }
            }
          >
            {Array.from(new Array(numPages), (el, index) => (
              <div>
                <Page
                  className="my-3"
                  key={`page_${index + 1}`}
                  width={600}
                  pageNumber={index + 1}
                />
              </div>
            ))}
          </Document>
        )}
      </div>
    </Portal>
  );
}
